import React from "react";

import { formatCurrencyRange, programAllows, useNavigate, useTranslation } from "utils";

import { FOOTPRINTING_ROUTE, WALLET_ROUTE } from "consts";

import { FarmResponse } from "models";

import { HomeSegment } from "pages/app/home/hero/homeSegment";
import { NO_FIGURE, StatusCard } from "pages/app/home/status-cards/StatusCard";
import { PayoutRange } from "pages/app/home/status-cards/homePayoutSummary";
import { HomeFootprintingSummaryResult } from "pages/app/home/status-cards/useHomeFootprintingSummary";
import { useHomePayoutSummary } from "pages/app/home/status-cards/useHomePayoutSummary";

type PayoutCardProps = {
  farm: FarmResponse;
  segment: HomeSegment;
  reportingYear: number;
  hasFields: boolean;
  footprinting: HomeFootprintingSummaryResult;
  /** data the card depends on is still loading (fields, reporting year) */
  loading?: boolean;
  /** the field list failed to load: nothing about the payout is known */
  unavailable?: boolean;
};

export const PayoutCard = ({ farm, segment, reportingYear, hasFields, footprinting, loading = false, unavailable = false }: PayoutCardProps) => {
  const { t } = useTranslation("home");
  const navigate = useNavigate();
  const { summary, loading: summaryLoading } = useHomePayoutSummary(farm, segment, reportingYear, footprinting, hasFields);

  const money = ({ min, max }: PayoutRange) => formatCurrencyRange(min, max, { decimals: 0, currency: summary.currency });
  const pendingReason = unavailable ? "unavailable" : summary.pendingReason;
  const known = !unavailable && summary.status === "known";
  const pending = unavailable || summary.status === "pending";
  const notAvailable = pendingReason === "unavailable";
  const partValue = (part: PayoutRange | "pending") => (part === "pending" ? t("statusCards.payout.pendingPart") : money(part));

  const breakdown =
    (known || pending) && hasFields && !notAvailable
      ? [
          ...(summary.fromFieldManagement !== null
            ? [{ label: t("statusCards.payout.fromFieldManagement"), value: partValue(summary.fromFieldManagement) }]
            : []),
          ...(summary.fromFootprinting !== null ? [{ label: t("statusCards.payout.fromFootprinting"), value: partValue(summary.fromFootprinting) }] : []),
        ]
      : [];

  // no caption while no field has complete data, nor when a request behind the figures failed
  const pendingCaptions = {
    allotment: "statusCards.payout.pendingAllotment",
    data: null,
    outcomes: "statusCards.payout.pending",
    unavailable: null,
  } as const;
  const pendingCaptionKey = pending ? pendingCaptions[pendingReason ?? "outcomes"] : null;
  const caption = pendingCaptionKey ? t(pendingCaptionKey) : undefined;
  const unit = known ? t("statusCards.payout.unit") : notAvailable ? t("statusCards.unavailable") : pending ? t("statusCards.payout.pendingUnit") : undefined;

  return (
    <StatusCard
      dataTestId="home-payout-card"
      featured
      loading={loading || summaryLoading}
      label={t("statusCards.payout.label")}
      value={known && summary.total ? money(summary.total) : NO_FIGURE}
      valueMuted={pending}
      unit={unit}
      caption={caption}
      breakdown={breakdown}
      cta={{
        label: t("statusCards.payout.cta"),
        variant: "circle",
        onClick: () => navigate(programAllows(farm.carbonProgram, "payout") ? WALLET_ROUTE : FOOTPRINTING_ROUTE),
      }}
    />
  );
};
